// /admin — the internal dashboard (English-only by design). One password,
// one signed cookie: any 401 from any call flips the page back to the login
// card. Three layers of UI state: login → invitation table (+ new-invite
// form) → row detail. Table/badge idioms follow careers.co.jp's admin
// routes; all colors come from the shared tokens.

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Sparkles } from "lucide-react";
import { Card, CardBody } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input, Label } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { BigFiveChart } from "@/components/bigfive-chart";
import { FacetReport } from "@/components/facet-report";
import { Page } from "@/components/page";
import { ThemeToggle } from "@/components/theme-toggle";
import { DOMAIN_META } from "@/lib/bigfiveMeta";
import {
  api,
  ApiError,
  formatMmSs,
  type AdminInvitation,
  type AdminInvitationDetail,
  type EventCounts,
  type ShareLink,
} from "@/lib/api";
import type { BigFiveTrait } from "@careers/shared";

// Per-page outlier thresholds for the timing breakdown (seconds). Below the
// floor a page was answered suspiciously fast; above the ceiling the
// candidate lingered (or left the tab visible and walked away).
const PAGE_FAST_SECONDS = 20;
const PAGE_SLOW_SECONDS = 300;

const SHARE_DAY_OPTIONS = [1, 7, 30, 90];

type Auth = "checking" | "loggedOut" | "loggedIn";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString();
}

function StatusBadge({ status }: { status: AdminInvitation["status"] }) {
  if (status === "completed") return <Badge variant="ok">Completed</Badge>;
  if (status === "pending") return <Badge variant="warn">Pending</Badge>;
  return <Badge variant="neutral">Void</Badge>;
}

// Human labels for the backend's timing cross-check reasons
// (crossCheckTimings in packages/backend/src/services/invitations.ts).
// Unknown reasons fall back to the raw slug so new flags still show up.
const SUSPECT_LABELS: Record<string, string> = {
  no_start: "never opened the test page",
  missing_timings: "timing data missing or incomplete",
  active_exceeds_wall: "claimed active time exceeds real elapsed time",
  perpage_mismatch: "per-page times don't add up",
};

/** Small amber "!" next to a time whose self-reported numbers failed the
 *  server cross-check. Hover lists the reasons; the detail spells them out. */
function SuspectMark({ reasons }: { reasons: string[] }) {
  return (
    <span
      title={reasons.map((r) => SUSPECT_LABELS[r] ?? r).join(" · ")}
      className="ml-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-warn-tint text-[11px] font-bold text-warn-on"
      aria-label="timing suspect"
    >
      !
    </span>
  );
}

/** Flag count with the per-type breakdown on hover. */
function Flags({ counts }: { counts: EventCounts }) {
  const total = counts.copy + counts.hidden + counts.blur;
  return (
    <span
      title={`copy ${counts.copy} · hidden ${counts.hidden} · blur ${counts.blur}`}
      className={
        total > 0 ? "font-semibold text-warn-on tabular-nums" : "text-text-3 tabular-nums"
      }
    >
      {total}
    </span>
  );
}

/** The five domain percentiles as compact labeled micro-bars. */
function MiniScores({ percentiles }: { percentiles: Record<BigFiveTrait, number> }) {
  return (
    <div className="flex items-end gap-1.5">
      {DOMAIN_META.map((d) => {
        const v = Math.max(0, Math.min(100, Math.round(percentiles[d.key] ?? 0)));
        return (
          <div key={d.key} title={`${d.en}: ${v}`} className="w-7">
            <div className="text-center text-[10px] tabular-nums text-text-2">{v}</div>
            <div className="h-1 overflow-hidden rounded-full bg-surface-2">
              <div
                className="h-full rounded-full"
                style={{ width: `${v}%`, backgroundColor: d.color }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function AdminPage() {
  const [auth, setAuth] = useState<Auth>("checking");
  const [invitations, setInvitations] = useState<AdminInvitation[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminInvitationDetail | null>(null);

  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState(false);

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);

  const [actionBusy, setActionBusy] = useState(false);
  const [shareDays, setShareDays] = useState(7);
  const [notice, setNotice] = useState<string | null>(null);

  /** Same-origin fetch that downgrades to the login card on any 401. */
  async function adminApi<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
    try {
      return await api<T>(path, init);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setAuth("loggedOut");
        setInvitations(null);
        setSelectedId(null);
        setDetail(null);
      }
      throw err;
    }
  }

  async function refreshList(): Promise<void> {
    const out = await adminApi<{ invitations: AdminInvitation[] }>("/api/admin/invitations");
    setInvitations(out.invitations);
    setAuth("loggedIn");
  }

  // Mount: probing the list endpoint doubles as the session check — a live
  // cookie loads the dashboard, a 401 shows the login card.
  useEffect(() => {
    refreshList().catch(() => {
      // 401 already handled by adminApi; anything else also lands on login,
      // where retrying is one click away.
      setAuth((cur) => (cur === "loggedIn" ? cur : "loggedOut"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the detail whenever a row is selected.
  useEffect(() => {
    if (!selectedId || auth !== "loggedIn") return;
    setDetail(null);
    setNotice(null);
    let cancelled = false;
    (async () => {
      try {
        const out = await adminApi<{ invitation: AdminInvitationDetail }>(
          `/api/admin/invitations/${selectedId}`,
        );
        if (!cancelled) setDetail(out.invitation);
      } catch {
        if (!cancelled) setSelectedId(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, auth]);

  async function reloadDetail(id: string): Promise<void> {
    const out = await adminApi<{ invitation: AdminInvitationDetail }>(
      `/api/admin/invitations/${id}`,
    );
    setDetail(out.invitation);
  }

  async function handleLogin(e: FormEvent): Promise<void> {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError(false);
    try {
      await api<{ ok: boolean }>("/api/admin/login", { method: "POST", body: { password } });
      setPassword("");
      await refreshList();
    } catch {
      setLoginError(true);
    } finally {
      setLoggingIn(false);
    }
  }

  async function handleLogout(): Promise<void> {
    try {
      await api<null>("/api/admin/logout", { method: "POST" });
    } catch {
      // Even if the call fails, drop to the login card; the cookie will
      // expire on its own.
    }
    setAuth("loggedOut");
    setInvitations(null);
    setSelectedId(null);
    setDetail(null);
  }

  async function handleInvite(e: FormEvent): Promise<void> {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    setInviting(true);
    setInviteError(false);
    try {
      await adminApi<{ invitation: AdminInvitation }>("/api/admin/invitations", {
        method: "POST",
        body: { email: address },
      });
      setEmail("");
      await refreshList();
    } catch {
      setInviteError(true);
    } finally {
      setInviting(false);
    }
  }

  function copyLink(row: { id: string; url: string | null }): void {
    if (!row.url) return;
    navigator.clipboard
      .writeText(row.url)
      .then(() => {
        setCopiedId(row.id);
        if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
        copiedTimer.current = window.setTimeout(() => setCopiedId(null), 1500);
      })
      .catch(() => {
        // Clipboard blocked — the admin can still open the detail and copy by hand.
      });
  }

  async function handleResend(d: AdminInvitationDetail): Promise<void> {
    if (!window.confirm(`Re-send the invitation email to ${d.email}?`)) return;
    setActionBusy(true);
    setNotice(null);
    try {
      await adminApi<{ ok: boolean }>(`/api/admin/invitations/${d.id}/resend`, { method: "POST" });
      setNotice("Invitation email re-sent.");
    } catch {
      setNotice("Resend failed — the invitation may no longer be pending.");
      await reloadDetail(d.id).catch(() => undefined);
    } finally {
      setActionBusy(false);
    }
  }

  async function handleVoid(d: AdminInvitationDetail): Promise<void> {
    if (!window.confirm(`Void this invitation? The link sent to ${d.email} will stop working.`)) {
      return;
    }
    setActionBusy(true);
    setNotice(null);
    try {
      await adminApi<{ ok: boolean }>(`/api/admin/invitations/${d.id}/void`, { method: "POST" });
      await Promise.all([reloadDetail(d.id), refreshList()]);
      setNotice("Invitation voided.");
    } catch {
      setNotice("Void failed — the invitation may no longer be pending.");
      await reloadDetail(d.id).catch(() => undefined);
    } finally {
      setActionBusy(false);
    }
  }

  async function handleCreateShareLink(d: AdminInvitationDetail): Promise<void> {
    setActionBusy(true);
    setNotice(null);
    try {
      await adminApi<{ shareLink: ShareLink }>(`/api/admin/invitations/${d.id}/share-links`, {
        method: "POST",
        body: { days: shareDays },
      });
      await reloadDetail(d.id);
    } catch {
      setNotice("Creating the share link failed.");
    } finally {
      setActionBusy(false);
    }
  }

  async function handleRevokeShareLink(d: AdminInvitationDetail, link: ShareLink): Promise<void> {
    if (!window.confirm("Revoke this share link? Anyone holding it will lose access.")) return;
    setActionBusy(true);
    setNotice(null);
    try {
      await adminApi<{ ok: boolean }>(
        `/api/admin/invitations/${d.id}/share-links/${link.id}/revoke`,
        { method: "POST" },
      );
    } catch {
      setNotice("Revoking the share link failed.");
    } finally {
      await reloadDetail(d.id).catch(() => undefined);
      setActionBusy(false);
    }
  }

  // "Invite again" = a fresh invitation for the same email (the old row and
  // its results stay). If another link for that email is still live, offer
  // to void it first so at most one live link exists per candidate;
  // declining aborts the whole action.
  async function handleInviteAgain(d: AdminInvitationDetail): Promise<void> {
    const pendingDup = invitations?.find(
      (r) => r.email === d.email && r.status === "pending" && r.id !== d.id,
    );
    if (pendingDup) {
      if (
        !window.confirm(
          `${d.email} already has a pending invitation. Void that link and send a new one?`,
        )
      ) {
        return;
      }
    }
    setActionBusy(true);
    setNotice(null);
    try {
      if (pendingDup) {
        await adminApi<{ ok: boolean }>(`/api/admin/invitations/${pendingDup.id}/void`, {
          method: "POST",
        });
      }
      await adminApi<{ invitation: AdminInvitation }>("/api/admin/invitations", {
        method: "POST",
        body: { email: d.email },
      });
      await refreshList();
      // Jump back to the table so the new pending row is visible.
      setSelectedId(null);
    } catch {
      setNotice("Creating the new invitation failed.");
    } finally {
      setActionBusy(false);
    }
  }

  // ----- Screens -----------------------------------------------------------

  if (auth === "checking") {
    return (
      <Page width="wide">
        <div className="space-y-3">
          <Skeleton className="h-10 w-64 rounded-md" />
          <Skeleton className="h-24 w-full rounded-md" />
          <Skeleton className="h-64 w-full rounded-md" />
        </div>
      </Page>
    );
  }

  if (auth === "loggedOut") {
    return (
      <Page className="flex min-h-[70vh] flex-col">
        {/* Keep the toggle top-right even on the login card, so the choice
            can be made before signing in. */}
        <div className="mb-6 flex justify-end">
          <ThemeToggle />
        </div>
        <div className="flex flex-1 items-center">
          <Card className="w-full">
            <CardBody className="space-y-4 py-6">
              <div>
                <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-text-2">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                  AGP · Big Five
                </p>
                <h1 className="font-serif text-2xl mt-2">Admin</h1>
              </div>
              <form onSubmit={(e) => void handleLogin(e)} className="space-y-3">
                <div>
                  <Label htmlFor="admin-password">Password</Label>
                  <Input
                    id="admin-password"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
                {loginError ? (
                  <p className="text-xs text-danger-on">Wrong password.</p>
                ) : null}
                <Button type="submit" disabled={loggingIn || password.length === 0}>
                  Sign in
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      </Page>
    );
  }

  // ----- Detail view -------------------------------------------------------

  if (selectedId) {
    return (
      <Page width="wide">
        <div className="mb-5 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}>
            ← All invitations
          </Button>
          <div className="flex items-center gap-1.5">
            <ThemeToggle />
            <Button variant="secondary" size="sm" onClick={() => void handleLogout()}>
              Log out
            </Button>
          </div>
        </div>

        {!detail ? (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full rounded-md" />
            <Skeleton className="h-72 w-full rounded-md" />
            <Skeleton className="h-40 w-full rounded-md" />
          </div>
        ) : (
          <>
            <Card>
              <CardBody className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="font-serif text-2xl">{detail.email}</h1>
                  <StatusBadge status={detail.status} />
                </div>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3 lg:grid-cols-5">
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-text-2">Invited</dt>
                    <dd title={formatDateTime(detail.invitedAt)}>{formatDate(detail.invitedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-text-2">Started</dt>
                    <dd title={detail.startedAt ? formatDateTime(detail.startedAt) : undefined}>
                      {detail.startedAt ? formatDate(detail.startedAt) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-text-2">Completed</dt>
                    <dd title={detail.completedAt ? formatDateTime(detail.completedAt) : undefined}>
                      {detail.completedAt ? formatDate(detail.completedAt) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-text-2">Language</dt>
                    <dd className="uppercase">{detail.lang ?? "—"}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] uppercase tracking-wide text-text-2">Flags</dt>
                    <dd>
                      <Flags counts={detail.eventCounts} />
                    </dd>
                  </div>
                </dl>
                <div className="flex flex-wrap items-center gap-2">
                  {detail.status === "pending" ? (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={actionBusy}
                        onClick={() => void handleResend(detail)}
                      >
                        Resend email
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={actionBusy}
                        onClick={() => void handleVoid(detail)}
                      >
                        Void link
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      disabled={actionBusy}
                      onClick={() => void handleInviteAgain(detail)}
                    >
                      Invite again
                    </Button>
                  )}
                  {notice ? <span className="text-xs text-text-2">{notice}</span> : null}
                </div>
              </CardBody>
            </Card>

            {detail.results ? (
              <div className="mt-5">
                <BigFiveChart scores={detail.results.percentiles} lang="en" />
                <Card className="mt-5">
                  <CardBody>
                    <p className="text-sm text-ink-soft leading-relaxed">
                      {detail.results.summary}
                    </p>
                  </CardBody>
                </Card>
                <FacetReport facets={detail.results.facets} lang="en" />
                <Card className="mt-5">
                  <CardBody className="space-y-3">
                    <h2 className="font-serif text-xl">Share results</h2>
                    <p className="text-sm text-text-2">
                      Anyone with a share link can view these results (read-only) until it
                      expires or you revoke it.
                    </p>
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <Label htmlFor="share-days">Expires after</Label>
                        <select
                          id="share-days"
                          value={shareDays}
                          onChange={(e) => setShareDays(Number(e.target.value))}
                          className="h-9 rounded-md border border-border bg-surface px-3 text-[14px] text-text"
                        >
                          {SHARE_DAY_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n === 1 ? "1 day" : `${n} days`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <Button
                        size="sm"
                        disabled={actionBusy}
                        onClick={() => void handleCreateShareLink(detail)}
                      >
                        Create share link
                      </Button>
                    </div>
                    {detail.shareLinks.length > 0 ? (
                      <ul className="space-y-1.5 text-sm">
                        {detail.shareLinks.map((link) => (
                          <li key={link.id} className="flex flex-wrap items-center gap-2.5">
                            <span className="text-text-2">
                              Expires {formatDateTime(link.expiresAt)}
                            </span>
                            <Button size="sm" variant="secondary" onClick={() => copyLink(link)}>
                              {copiedId === link.id ? "Copied" : "Copy link"}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={actionBusy}
                              onClick={() => void handleRevokeShareLink(detail, link)}
                            >
                              Revoke
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </CardBody>
                </Card>
              </div>
            ) : (
              <Card className="mt-5">
                <CardBody>
                  <p className="text-sm text-text-2">No results yet — the test has not been completed.</p>
                </CardBody>
              </Card>
            )}

            <Card className="mt-5">
              <CardBody className="space-y-3">
                <h2 className="font-serif text-xl">Timing</h2>
                {/* Two clocks on purpose: active time is the candidate's own
                    claim, wall time comes from the server's start/finish
                    stamps and cannot be faked. A big gap between them is
                    itself informative. */}
                <p className="text-sm">
                  Active time (self-reported):{" "}
                  <span className="tabular-nums font-medium">
                    {detail.activeSeconds != null ? formatMmSs(detail.activeSeconds) : "—"}
                  </span>
                  <span className="text-text-3"> (clock pauses while the tab is hidden)</span>
                </p>
                <p className="text-sm">
                  Wall time (server-verified):{" "}
                  <span className="tabular-nums font-medium">
                    {detail.wallSeconds != null ? formatMmSs(detail.wallSeconds) : "—"}
                  </span>
                  <span className="text-text-3"> (first questions fetch → submission)</span>
                </p>
                {detail.timingSuspect.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {detail.timingSuspect.map((reason) => (
                      <Badge key={reason} variant="warn">
                        {SUSPECT_LABELS[reason] ?? reason}
                      </Badge>
                    ))}
                  </div>
                ) : null}
                {detail.timings &&
                Array.isArray(detail.timings.perPage) &&
                detail.timings.perPage.length > 0 ? (
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-3">
                    {detail.timings.perPage.map((seconds, i) => {
                      const fast = seconds < PAGE_FAST_SECONDS;
                      const slow = seconds > PAGE_SLOW_SECONDS;
                      return (
                        <li key={i} className="flex items-center gap-2">
                          <span className="w-14 text-text-2">Page {i + 1}</span>
                          <span
                            className={
                              fast || slow
                                ? "tabular-nums font-semibold text-warn-on"
                                : "tabular-nums"
                            }
                          >
                            {formatMmSs(seconds)}
                          </span>
                          {fast ? <Badge variant="warn">fast</Badge> : null}
                          {slow ? <Badge variant="warn">slow</Badge> : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </CardBody>
            </Card>

            <Card className="mt-5">
              <CardBody className="space-y-3">
                <h2 className="font-serif text-xl">Event log</h2>
                {detail.events.length === 0 ? (
                  <p className="text-sm text-text-2">No events recorded.</p>
                ) : (
                  <ul className="space-y-1.5 text-sm">
                    {detail.events.map((evt, i) => (
                      <li key={i} className="flex items-center gap-2.5">
                        <Badge variant={evt.type === "copy" ? "danger" : "neutral"}>
                          {evt.type}
                        </Badge>
                        <span className="tabular-nums text-text-2">
                          {formatDateTime(evt.occurredAt)}
                        </span>
                        {evt.meta != null ? (
                          <span className="truncate font-mono text-[11px] text-text-3">
                            {JSON.stringify(evt.meta)}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </>
        )}
      </Page>
    );
  }

  // ----- Table + new-invitation form --------------------------------------

  const rows = invitations;

  return (
    <Page width="wide">
      <header className="mb-5 flex items-end justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.18em] text-text-2">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            AGP · Big Five
          </p>
          <h1 className="font-serif text-3xl mt-1">Invitations</h1>
        </div>
        <div className="flex items-center gap-1.5">
          <ThemeToggle />
          <Button variant="secondary" size="sm" onClick={() => void handleLogout()}>
            Log out
          </Button>
        </div>
      </header>

      <Card className="mb-4">
        <CardBody>
          <form onSubmit={(e) => void handleInvite(e)} className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px] flex-1">
              <Label htmlFor="invite-email">New invitation</Label>
              <Input
                id="invite-email"
                type="email"
                required
                placeholder="candidate@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <Button type="submit" disabled={inviting || email.trim().length === 0}>
              Send invitation
            </Button>
            {inviteError ? (
              <p className="basis-full text-xs text-danger-on">
                Could not create the invitation — check the email address.
              </p>
            ) : null}
          </form>
        </CardBody>
      </Card>

      {rows === null ? (
        <div className="space-y-2.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <CardBody>
            <p className="text-sm text-text-2">
              No invitations yet — send the first one above.
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-surface shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-surface-2/40 text-left text-[11px] uppercase tracking-wide text-text-2">
              <tr>
                <th className="py-2 px-3 font-medium">Email</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Invited</th>
                <th className="py-2 px-3 font-medium">Completed</th>
                <th className="py-2 px-3 font-medium">Time</th>
                <th className="py-2 px-3 font-medium">Flags</th>
                <th className="py-2 px-3 font-medium">Scores</th>
                <th className="py-2 px-3 font-medium">Link</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  className="cursor-pointer border-t border-border hover:bg-surface-2/40"
                >
                  <td className="py-2.5 px-3 font-medium">{r.email}</td>
                  <td className="py-2.5 px-3">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="py-2.5 px-3 text-text-2" title={formatDateTime(r.invitedAt)}>
                    {formatDate(r.invitedAt)}
                  </td>
                  <td
                    className="py-2.5 px-3 text-text-2"
                    title={r.completedAt ? formatDateTime(r.completedAt) : undefined}
                  >
                    {r.completedAt ? formatDate(r.completedAt) : "—"}
                  </td>
                  <td className="py-2.5 px-3 tabular-nums">
                    {r.activeSeconds != null ? formatMmSs(r.activeSeconds) : "—"}
                    {r.timingSuspect.length > 0 ? (
                      <SuspectMark reasons={r.timingSuspect} />
                    ) : null}
                  </td>
                  <td className="py-2.5 px-3">
                    <Flags counts={r.eventCounts} />
                  </td>
                  <td className="py-2.5 px-3">
                    {r.percentiles ? (
                      <MiniScores percentiles={r.percentiles} />
                    ) : (
                      <span className="text-text-3">—</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    {r.url ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyLink(r);
                        }}
                      >
                        {copiedId === r.id ? "Copied" : "Copy link"}
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Page>
  );
}
